// Local: selektor daty z językiem kalendarza z i18n
// @ts-nocheck

import React, { FC, useEffect, useState, MouseEvent, ChangeEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { parseISO, format, setHours, setMinutes } from 'date-fns';
import { pl, enUS } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import Popover from '@mui/material/Popover';
import { Calendar } from 'react-bootstrap-icons';
import classnames from 'classnames';

import { CloseIcon } from 'assets/icons';

import styles from 'theme/components/controls/DatePicker/DatePicker.module.scss';
import 'react-day-picker/dist/style.css';

const localeMap: Record<string, typeof pl> = { pl, en: enUS };

interface IProps {
  date?: string;
  onChange?: (date: string | null) => void;
  clearable?: boolean;
  isTimePicker?: boolean;
  disabled?: boolean;
}

const DatePicker: FC<IProps> = ({ date, onChange, clearable, isTimePicker, disabled }) => {
  const { i18n } = useTranslation();
  const locale = localeMap[i18n.language] ?? enUS;

  const [localDate, setLocalDate] = useState<Date>();
  const [timeValue, setTimeValue] = useState<string>(
    date ? format(new Date(date), 'HH:mm') : '00:00'
  );
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setTimeValue(date ? format(new Date(date), 'HH:mm') : '00:00');
    setDateFromProps();
  }, [date]);

  const setDateFromProps = () => {
    setLocalDate(date ? parseISO(date) : undefined);
  };

  const handleOnSelectDate = (selectedDate: Date) => {
    if (isTimePicker) {
      const [hours, minutes] = timeValue.split(':').map((str) => parseInt(str, 10));
      const newSelectedDate = setHours(setMinutes(selectedDate || new Date(), minutes), hours);
      setLocalDate(newSelectedDate);
      onChange?.(format(newSelectedDate, 'yyyy-MM-dd HH:mm'));
    } else {
      setLocalDate(selectedDate);
      onChange?.(format(selectedDate, 'yyyy-MM-dd'));
    }
    setPopoverAnchor(null);
  };

  const handleOnClosePopover = () => {
    setPopoverAnchor(null);
    isTimePicker && localDate
      ? onChange?.(format(localDate, 'yyyy-MM-dd HH:mm'))
      : setDateFromProps();
  };

  const handleClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange?.(null);
  };

  const handleTimeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value;
    const [hours, minutes] = time.split(':').map((str) => parseInt(str, 10));
    const newSelectedDate = setHours(setMinutes(localDate || new Date(), minutes), hours);
    setLocalDate(newSelectedDate);
    setTimeValue(time);
  };

  return (
    <>
      <button
        className={classnames(
          'StylePath-Components-Controls-DatePicker',
          styles.button,
          disabled && styles.disabled
        )}
        onClick={(event) => setPopoverAnchor(event.currentTarget)}>
        {localDate ? (
          <span>{format(localDate, isTimePicker ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy')}</span>
        ) : (
          <span className={styles.placeholder}>{!disabled && <Trans>Wybierz datę</Trans>}</span>
        )}
        {clearable && !disabled && (
          <button className={styles.clear} onClick={handleClear}>
            <CloseIcon />
          </button>
        )}
        <Calendar {...(disabled && { color: '#8D9096' })} />
      </button>

      <Popover
        anchorEl={popoverAnchor}
        open={!!popoverAnchor && !disabled}
        onClose={handleOnClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}>
        {isTimePicker && (
          <div className={styles.timePicker}>
            <input type="time" value={timeValue} onChange={handleTimeChange} />
          </div>
        )}

        <DayPicker
          locale={locale}
          selected={localDate}
          defaultMonth={localDate}
          onDayClick={(date?: Date) => date && handleOnSelectDate(date)}
        />
      </Popover>
    </>
  );
};

export default DatePicker;
